import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Language } from './models/core.models';
import { Op } from 'quill';
import { environment } from '@env/environment';

/**
 * nom_algorithm_task_status — status of an external algorithm / pipeline run.
 */
export enum NomAlgorithmTaskStatus {
  PENDING = 0,
  PROCESSING = 1,
  COMPLETED = 2,
  FAILED = 3,
}

export enum NomObjectLifecycleStatus {
  ALL = 0,
  /**
   * @description Active
   */
  ACTIVE = 1,

  /**
   * @description Template
   */
  TEMPLATE = 2,

  /**
   * @description Archived
   */
  ARCHIVED = 3,

  /**
   * @description Deleted
   */
  DELETED = 4,

  /**
   * @description Draft
   */
  DRAFT = 5,

  /**
   * @description Review
   */
  REVIEW = 6,

  /**
   * @description Inactive
   */

  INACTIVE = 7,
}

export enum AppVersion {
  V3 = 3,
}

/**
 * @description nom_object_class
 * @author Auto-generated constant (generate_constants.php)
 */
/** `nom_view_preset_scope`: who owns a View Preset. Id 20 (Agency) is reserved and not served yet. */
export enum NomViewPresetScope {
  SYSTEM = 10,
  COMPANY = 30,
  USER = 40,
}

export enum NomObjectClass {
  FEED = 1,
  WORKSPACE = 2,
  COMPANY = 7,
  LABEL = 8,
  LOCATION = 9,
  POST = 10,
  USER = 11,
  ROLE = 12,
  SECTION = 15,
  SOURCE = 16,
  NOTIFICATION = 17,
  COLLECTION = 18,
  CACHE = 19,
  ALERT = 20,
  DASHBOARD = 21,
  EXPORT_SCHEDULE = 22,
  MANAGED_SOURCE_POST = 23,
  CONNECTOR = 24,
  RULE = 25,
  CREDENTIALS = 26,
  ENTITY = 27,
  WIDGET = 28,
  USER_GROUP = 29,
  OUTPUT_SETTINGS = 30,
  ENTITY_METRIC = 31,
  COLOR_PALETTE = 33,
  EXPORT_TEMPLATE = 36,
  DATA_PROVIDER = 37,
}

/**
 * Object classes an export schedule can be attached to (the export settings
 * panel lives on feeds and dashboards). Drives the "Attached to" set filter of
 * the schedules grids so internal classes never show up there (WAP-1657).
 */
export const EXPORT_SCHEDULE_CONTEXT_OBJECT_CLASSES: ReadonlyArray<NomObjectClass> =
  [NomObjectClass.FEED, NomObjectClass.DASHBOARD];
export enum NomObjectTypes {
  UNKNOWN = null,
  ONLINE_FEED = 1,
  CLIPPING_FEED = 2,
  SOCIAL_FEED = 4,
  COMBINED_FEED = 5,
  SEARCH_DIMENSION = 7,
  SOURCE_CATALOG_FEED = 10,
  BROADCAST_FEED = 20,
  STORY_FEED = 30,

  WORKSPACE_TYPE_FEEDS = 11,
  WORKSPACE_TYPE_CATALOG = 15,

  DASHBOARD_TYPE = 20,
  DASHBOARD = 22,
}

export enum NomMediaChannel {
  ALL = 0,
  ONLINE = 1,
  SOCIAL = 2,
  PRINT = 3,
  BROADCAST = 4,
}

export enum NomConnectorObjectType {
  UNKNOWN = null,
  CRITICAL_MENTION = 1,
  FACEBOOK_INSIGHTS = 2,
  GOOGLE_ANALYTICS = 3,
  TWITTER = 4, // X (Twitter)
  EMEDIA_MONITOR = 5,
  SENTINEL_TRAILS = 6,
  TELEGRAM = 7,
  INSTAGRAM = 8,
  TIKTOK = 9,
  YOUTUBE = 10,
  FACEBOOK = 11,
  VKONTAKTE = 12,
  H_TECH = 13,
  LINKEDIN = 14,
  CROSS_DISPLAY = 15,
  VETRIC = 17, // Vetric API (X)
  VETRIC_TIKTOK = 18, // Vetric API (TikTok)
  REDDIT = 19, // Reddit through the Vetric vendor (SEN-6039, WAP-2035)
}

export enum DimensionJoinTypeId {
  AND = 1,
  AND_NOT = 2,
}

export enum DimensionAttachTypeId {
  FILTER = 1,
  FAVORITE = 2,
}

export enum WidgetType {
  WIDGET_TYPE_CHART = 1,
  WIDGET_TYPE_TABLE = 2,
  WIDGET_TYPE_POST_LIST = 3,
  WIDGET_TYPE_GEO_MAP = 4,
  WIDGET_TYPE_SUMMARY = 5,
  WIDGET_TYPE_CSV = 6,
  WIDGET_TYPE_EXPORT_VARIABLES_USER = 7,
  WIDGET_TYPE_EXPORT_VARIABLES_SYSTEM = 8,
  WIDGET_TYPE_SECTION_BREAK = 9,
  WIDGET_TYPE_PLACEHOLDER = 10,
  WIDGET_TYPE_HTML = 11,
}

export enum NomLanguages {
  DEFAULT = 0,
  ENGLISH = 1,
  ARABIC = 6,
  BULGARIAN = 12,
}

export enum NomTimezones {
  EUROPE_SOFIA = 357,
}

export enum NomRoles {
  DEFAULT_ADMIN_ROLE_ID = 33, // System - Company Admin
  DEFAULT_USER_ROLE_ID = 36, // System - Company Default User
}

export enum NomSocialSource {
  FACEBOOK = 1,
  X = 2,
  YOUTUBE = 3,
  INSTAGRAM = 4,
  LINKEDIN = 5,
  TELEGRAM = 6,
  TIKTOK = 7,
  REDDIT = 9,
}

export enum NomSentiment {
  NEUTRAL = 1,
  POSITIVE = 2,
  NEGATIVE = 3,
  AVERAGE = 99,
}

export enum NomOutputFormat {
  DOCX = 1,
  CSV = 2,
  HTML = 3,
  MATTERMOST = 4,
  PDF = 5,
}

// nom_output_format_settings rows referenced by id in the Output Settings format
// editor. Ids are pinned by bin/fixers/WAP-1515/01-seed_bilingual_header_settings.php
// in the backend repo - keep the two in sync.
export enum NomOutputFormatSettings {
  DOCX_BILINGUAL_HEADERS = 40,
  DOCX_BILINGUAL_HEADERS_LANGUAGE = 41,
}

export enum NomExternalShareOption {
  CONTENT_EXCERPT = 1,
  FULL_CONTENT = 2,
  AI_SUMMARY = 3,
}

export enum NomAlertStatus {
  Triggered = 1,
  Shown = 2,
  Confirmed = 3,
  Unsubscribed = 4,
}

export enum NomAlertTriggerType {
  NEW_POSTS = 1,
  PERCENT_CHANGE = 2,
}

export enum NomAlertDeviationDirection {
  Up = 1,
  Down = 2,
  UpOrDown = 3,
}

export enum NomMassUpdateOperation {
  ADD = 1,
  EDIT = 2,
  REMOVE = 3,
  DEFAULT = 4,
}

export enum NomAlgorithm {
  POST_RECONSUME = 552,
  POST_INSTAGRAM_DATA_SCRAPPER = 526,
  POST_TRANSLATE = 525,
  POST_REACH_REACALCULATE = 506,
  POST_LANGUAGE_DETECTION = 523,
  POST_REINDEX = 522,
  POST_HARVEST = 1,
  POST_FACE_DETECTION = 521,
  SET_SENTIMENT = 520,
  GENERATE_SCREEN_CAPTURE = 519,
  SET_CATEGORIES = 518,
  POST_RELATED_CONTENT_LINK_SIMILAR = 517,
  POST_RELATED_CONTENT_UNLINK = 557,
  POST_GENERATE_AI_SYNOPSIS = 516,
  POST_RELATED_CONTENT_EMBEDDINGS = 515,
  POST_VIDEOS_SPEECH_TO_TEXT_WHISPER = 514,
  PERSUASION_TECHNIQUE = 513,
  CONTENT_NAMED_ENTITIES_HISTORICAL = 511,
  POST_CONTENT_RECALCULATE_REACH = 506,
  POST_NAMED_ENTITY_TITLE = 55,
  LAB_SENTIMENT = 54,
  POST_CONTENT_INTERNAL_KEYPHRASES = 48,
  POST_CONTENT_ROSETTE = 47,
  POST_ESTIMATE_CONTENT_SENTIMENT = 46,
  POST_CONTENT_NAMED_ENTITY_SENTIMENT = 44,
  CONTENT_NAMED_ENTITIES = 30,
  CALCULATE_POST_AUTOKEYWORDS = 26,
  ESTIMATE_POST_CONTENT_TYPE = 2,
  CONTENT_HARVESTER = 1,
  AI_AUTO_STORY = -1, // ! not BE algorithm currently - temporary
  QUERY_AUTO_STORY = 543,
  POST_UPDATE_SOURCE_FAVICON = 560,

  // New IDs start here
  EXPORT_TO_WORD = 527,
  EXPORT_TO_EXCEL = 528,
  SHARE_TO_FEED = 529,
  EDIT_SENTIMENT = 530,
  EDIT_CATEGORIES = 531,

  SHARE = 5320,
}

export enum NomPostType {
  OnlineArticle = 1,
  FacebookPost = 2,
  TwitterPost = 3,
  YouTubeVideo = 4,
  InstagramPost = 5,
  LinkedinPost = 6,
  TelegramPost = 7,
  BroadcastTVVideo = 9,
  PrintNewspaperArticle = 10,
  BroadcastRadioAudio = 11,
  TikTokPost = 12,
}

export enum NomPostObjectType {
  Post = 1,
  Quote = 3,
  Reshare = 4,
  Story = 5,
}

/** `nom_admin_operation_module` (SEN-6207): the admin module whose ledger view lists a run. */
export enum NomAdminOperationModule {
  BIZ_ADMIN = 1,
  SYS_ADMIN = 2,
  SOURCE_CATALOG = 3,
}

export enum NomContentProvider {
  CriticalMention = 9,
  DatasiftFacebook = 7,
  DatasiftTwitter = 6,
  EMediaMonitor = 14,
  GnipTwitter = 4,
  HTech = 20,
  Instagram = 18,
  LinkedIn = 21,
  MediaZoom = 5,
  SensikaCustomRSSBuilder = 8,
  SensikaFacebook = 2,
  SensikaForum = 3,
  SensikaManual = 11,
  SensikaPrint = 10,
  SensikaRSS = 1,
  SensikaSitemap = 12,
  SensikaSitemapUrlSet = 13,
  SensikaYoutube = 15,
  Telegram = 16,
  TikTok = 17,
  VKontakte = 19,
}
export const NomSupportedLanguages: Language[] = [
  {
    id: NomLanguages.ENGLISH,
    name: 'English',
    code: 'en',
    rtl: false,
  },
  {
    id: NomLanguages.ARABIC,
    name: 'Arabic',
    code: 'ar',
    rtl: true,
  },
  {
    id: NomLanguages.BULGARIAN,
    name: 'Bulgarian',
    code: 'bg',
    rtl: false,
  },
];
export enum NomObjectPermissionRole {
  OWNER = 1,
  VIEWER = 2,
  EDITOR = 3,
  CUSTOM = 4,
  ADMIN = 5,
}

export enum NomObjectPermission {
  CAN_VIEW = 1,
  CAN_EDIT = 2,
  CAN_DELETE = 3,
  CAN_EXECUTE = 4,
  CAN_MANAGE = 5,
  CAN_ACTIVATE = 6,
  CAN_ARCHIVE = 7,
  CAN_AUDIT = 8,
  CAN_COMMIT = 9,
  CAN_CONFIG = 10,
  CAN_COPY = 11,
  CAN_CREATE = 12,
  CAN_DEACTIVATE = 13,
  CAN_EMAIL = 14,
  CAN_EXPORT = 15,
  CAN_FILTER = 16,
  CAN_LINK = 17,
  CAN_LOCK = 18,
  CAN_MOVE = 19,
  CAN_RESTART = 20,
  CAN_SCHEDULE = 21,
  CAN_UNARCHIVE = 22,
  CAN_UNLINK = 23,
  CAN_UNLOCK = 24,
  CAN_LIST = 25,
  CAN_CREATE_SEARCH_FEEDS = 26,
  CAN_CREATE_SHARING_FEEDS = 27,
  CAN_CREATE_COMBINED_FEEDS = 28,
  CAN_CREATE_PREMIUM_FEEDS = 29,
  CAN_CREATE_SOCIAL_FEEDS = 30,
  CAN_CREATE_CATALOG_FEEDS = 31,
  CAN_CREATE_DIMENSIONS = 32,
}

export enum NomScheduleTypes {
  DAILY = 1,
  HOURLY = 2,
  WEEKLY = 3,
  MONTHLY = 4,
}

/**
 * Sentinel for `schedule_day_of_month_to_send` meaning "the last calendar day of the
 * month", whatever that is for the month in question (28/29/30/31).
 */
export const SCHEDULE_DAY_OF_MONTH_LAST = 0;

export enum NomExportTransportTypes {
  FILE = 'file',
  EMAIL = 'email',
  WHATSAPP = 'whatsapp',
  LINKEDIN = 'linkedin',
  TELEGRAM = 'telegram',
  MATTERMOST = 'mattermost',
  FEED = 'feed',
  STORY = 'story',
}

export enum FaceRecognitionStatus {
  Unrecognized = 1,
  Ignored = 2,
  Recognized = 3,
}

export enum NomDateTimeFormats {
  ADMIN_FULL = 'yyyy-MM-dd HH:mm:ss',
  ADMIN_LONG = 'yyyy-MM-dd HH:mm',
  ADMIN_SHORT = 'yyyy-MM-dd',
  ANALYTICS_LONG = 'dd MMM yyyy, HH:mm',
  ANALYTICS_SHORT = 'dd MMM yyyy',
}

export const ImageFallbacks = {
  avatar: '/assets/default-avatar.png',
};

export const ImagePlaceholders = {
  spinner: environment.baseUrlEnvironmentSuffix + '/assets/img/spinner.gif',
};

export type MassOperationType = 'archive' | 'unarchive' | 'delete' | 'undelete';

export type SortDirection = 'asc' | 'desc';

/** The clipping feed's saved post sequence as a sort field (backend `manual` -> Solr `score`), WAP-1888. */
export const FEED_SORT_FIELD_MANUAL = 'manual';

/** Cap of the saved manual order, mirrors the backend `MANUAL_ORDER_MAX_POSTS`. */
export const FEED_MANUAL_ORDER_MAX_POSTS = 500;

export type AppSection = 'analytics' | 'admin' | 'source-catalog';

// TODO: we have to rename this anum once merged with the object picker dialog
export enum PopupType {
  // * Object picker related popup types
  SELECT_USERS = 'select-users',
  ADD_PRINCIPALS = 'add-principals',
  SELECT_NEW_OWNER = 'select-new-owner',
  SELECT_PRINCIPALS = 'select-principals',
  SELECT_DATASOURCES = 'select-datasources',
  SELECT_FAVORITE_DIMENSIONS = 'select-favorite-dimensions',
  ATTACH_DIMENSIONS_AS_PIVOTS = 'attach-dimensions-as-pivots',
  ATTACH_DIMENSIONS_AS_FILTERS = 'attach-dimensions-as-filters',
  ATTACH_FILTERS = 'attach-filters',
}

export interface PopupMetadata {
  // * translation key for action label
  action: string;
  actionIcon?: IconProp;
  popupType: PopupType;
}

export const DEFAULT_PPRESET_COLORS = [
  '#980000',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#4a86e8',
  '#0000ff',
  '#9900ff',
  '#ff00ff',
];

// TODO: we might consider creating constants files for business logic and UI/UX constants
// Owned by sui since WAP-1744 (the dialog stack needs them); re-exported so the app keeps one vocabulary entry.
export { ZIndex, EscapePriority } from '@sui/shared';

export type DeltaContent = { ops: Op[] };

/** Feed sort fields limited to specific feed object types (others apply everywhere). */
export const FEED_SORT_FIELD_APPLICABILITY: Record<string, NomObjectTypes[]> = {
  post_title: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  source_rank_global: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  source_rank_country: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_followers: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_reactions_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_interactions_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_engagements_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_shares_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_comments_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_likes_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_dislikes_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  [FEED_SORT_FIELD_MANUAL]: [NomObjectTypes.CLIPPING_FEED],
};

/** True when the sort field is valid for the given feed object type. */
export function feedSortFieldApplies(
  field: string,
  objectTypeId: NomObjectTypes,
): boolean {
  const applicable = FEED_SORT_FIELD_APPLICABILITY[field];
  return !applicable || applicable.includes(objectTypeId);
}

/**
 * Which date a feed's post cards show, derived from the feed's own settings - one
 * of the four `post_time_mode` values. Shared so the reducer (which seeds
 * `timeShown`) and the view-config query (which flags the matching radio) cannot
 * drift: the cards read whichever of the two their layout happens to use.
 */
export function feedDerivedTimeShown(feed: {
  post_time_field?: string;
  post_time_mode?: string;
}): string {
  const field =
    feed?.post_time_field === 'post_time_created'
      ? 'post_time_created'
      : 'post_time_published';

  return feed?.post_time_mode === 'relative'
    ? `${field}_relative`
    : `${field}_absolute`;
}
